import axios, { AxiosInstance, AxiosError } from "axios";
import i18n from "i18next";
import { ApiClientConfig, ApiError } from "./types";
import { logger } from "../../utils/logger";
import { dispatchHotelStateInvalidated } from "../../utils/hotelStateEvents";

const HOTEL_STATE_MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const HOTEL_STATE_MUTATING_URL_PATTERNS = [
  /\/api\/admin\/registration\b/i,
  /\/api\/admin\/account\b/i,
  // Onboarding mutations (hotel-details, contact-info) update Hotel + HotelSettings + StaffUser,
  // all of which are cached in HotelContext. /progress is harmless to refresh on too.
  /\/api\/admin\/onboarding\b/i,
];

/**
 * Backend error response shape - handles both .NET validation errors and custom error DTOs
 */
interface BackendErrorResponse {
  errors?: Record<string, string[]>;
  title?: string;
  detail?: string;
  message?: string;
  Message?: string; // C# convention
  errorCode?: string;
}

export const createApiClient = (config: ApiClientConfig): AxiosInstance => {
  const client = axios.create({
    baseURL: config.baseUrl,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor for auth and hotel selection
  client.interceptors.request.use(
    async (request) => {
      if (config.getAccessToken) {
        try {
          const token = await config.getAccessToken();
          request.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          logger.error("Error getting access token:", error);
        }
      }

      // Add X-Hotel-Id header for multi-hotel support
      // Read from localStorage (shared across all tabs)
      const selectedHotelId = localStorage.getItem("selectedHotelId");
      if (selectedHotelId) {
        request.headers["X-Hotel-Id"] = selectedHotelId;
      }

      // Add Accept-Language header for content translations
      const currentLanguage = i18n.language;
      if (currentLanguage) {
        request.headers["Accept-Language"] = currentLanguage;
      }

      return request;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor for error handling + cross-context state invalidation
  client.interceptors.response.use(
    (response) => {
      // Auto-invalidate HotelContext after successful admin mutations that may
      // affect cached hotel/user state. Prevents the "stale myHotelInfo after
      // registration" class of bug where callers forget to refresh context.
      // See: utils/hotelStateEvents.ts for the contract.
      if (!config.isGuest) {
        const method = response.config.method?.toUpperCase() ?? "";
        const url = response.config.url ?? "";
        if (
          HOTEL_STATE_MUTATING_METHODS.has(method) &&
          HOTEL_STATE_MUTATING_URL_PATTERNS.some((re) => re.test(url))
        ) {
          dispatchHotelStateInvalidated({ url, method });
        }
      }
      return response;
    },
    async (error: AxiosError) => {
      // Handle unauthorized errors
      if (error.response?.status === 401 && config.onUnauthorized) {
        config.onUnauthorized();
      }

      // For admin requests, we'll handle errors consistently
      if (!config.isGuest && error.response) {
        const apiError: ApiError = {
          status: error.response.status,
          message: error.response.statusText,
          details: error.response.data,
          apiResponse: error.response.data,
        };

        // Parse error message from response
        const data = error.response.data as BackendErrorResponse | undefined;
        if (data) {
          if (data.errors) {
            // Handle validation errors
            const validationErrors = Object.entries(data.errors)
              .map(
                ([key, messages]) =>
                  `${key}: ${(messages as string[]).join(", ")}`,
              )
              .join("; ");
            apiError.message =
              validationErrors || data.title || "Validation failed.";
          } else if (data.title) {
            apiError.message =
              data.title + (data.detail ? ` ${data.detail}` : "");
          } else if (data.message) {
            apiError.message = data.message;
          } else if (data.Message) {
            // Handle C# backend convention with capital M
            apiError.message = data.Message;
          } else if (data.errorCode) {
            apiError.message = data.errorCode;
          }
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
};

// Helper to parse axios errors into our ApiError format
export const parseAxiosError = (error: unknown): ApiError => {
  logger.error("API Error:", error);
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    if (axiosError.response) {
      const data = axiosError.response.data as BackendErrorResponse | undefined;
      let message = axiosError.response.statusText;

      // Try to extract a more specific error message
      if (data) {
        if (data.errors) {
          const validationErrors = Object.entries(data.errors)
            .map(
              ([key, messages]) =>
                `${key}: ${(messages as string[]).join(", ")}`,
            )
            .join("; ");
          message = validationErrors || data.title || "Validation failed.";
        } else if (data.title) {
          message = data.title + (data.detail ? ` ${data.detail}` : "");
        } else if (data.message) {
          message = data.message;
        } else if (data.Message) {
          // Handle C# backend convention with capital M
          message = data.Message;
        } else if (data.errorCode) {
          message = data.errorCode;
        }
      }

      return {
        status: axiosError.response.status,
        message,
        details: data,
        apiResponse: data,
      };
    } else if (axiosError.request) {
      return {
        status: 0,
        message: "No response from server",
        details: axiosError.message,
      };
    }
  }

  // Generic error
  return {
    status: 0,
    message: error instanceof Error ? error.message : "Unknown error occurred",
    details: error,
  };
};
